import { useMemo, useState } from 'react';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import OfferingsForm from '../../Offerings/OfferingsForm';
import { EMPTY_FORM, newItem } from '../../Offerings/offeringsConfig';
import { formatDate } from '../../../utils/dateUtils';
import { formatMoney } from './openingBalanceUtils';
import tableStyles from '../../Offerings/OfferingsPage.module.css';

const buildEmptyForm = (financialYearId, defaultDate = '') => ({
  ...EMPTY_FORM,
  financialYearId: financialYearId || '',
  offeringDate: defaultDate || '',
  items: [newItem()],
});

const buildFormFromLine = (line, categories, financialYearId) => {
  const category = categories.find((item) => String(item.id) === String(line.donationCategoryId));

  return {
    ...EMPTY_FORM,
    userId: line.userId || '',
    userName: line.userName || '',
    financialYearId: financialYearId || '',
    offeringDate: line.originalOfferingDate || '',
    notes: '',
    items: [{
      ...newItem(),
      donationCategoryId: line.donationCategoryId || '',
      categoryName: category?.name || '',
      qty: 1,
      rate: line.pendingAmountInr || '',
      amount: line.pendingAmountInr || '',
    }],
  };
};

export default function OfferingBalancesTab({
  fy,
  categories,
  personUsers,
  offeringRows,
  readOnly,
  frozen,
  saving,
  openingOfferDateMax,
  total,
  onAddRowWithData,
  onAddRowsWithData,
  onUpdateRow,
  onRemoveRow,
}) {
  const [view, setView] = useState('list');
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(() => buildEmptyForm(fy?.id, openingOfferDateMax || ''));

  const canWrite = !readOnly && !frozen;
  const financialYearOptions = useMemo(
    () => (fy ? [{ id: fy.id, yearLabel: fy.yearLabel, startDate: fy.startDate, endDate: fy.endDate, isClosed: false }] : []),
    [fy],
  );

  const openAdd = () => {
    setEditingKey(null);
    setForm(buildEmptyForm(fy?.id, openingOfferDateMax || ''));
    setView('form');
  };

  const openEdit = (key, line) => {
    setEditingKey(key);
    setForm(buildFormFromLine(line, categories, fy?.id));
    setView('form');
  };

  const closeForm = () => {
    setEditingKey(null);
    setView('list');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payloads = (form.items || [])
      .map((item) => ({
        userId: form.userId || '',
        userName: form.userName || '',
        donationCategoryId: item?.donationCategoryId || '',
        pendingAmountInr: item?.amount || '',
        originalOfferingDate: form.offeringDate || '',
      }))
      .filter((payload) => payload.userId && payload.donationCategoryId && Number(payload.pendingAmountInr) > 0);

    if (payloads.length === 0) return;

    if (editingKey) await onUpdateRow(editingKey, payloads[0]);
    else if (payloads.length === 1) await onAddRowWithData(payloads[0]);
    else await onAddRowsWithData(payloads);

    closeForm();
  };

  if (view === 'form') {
    return (
      <OfferingsForm
        form={form}
        setForm={setForm}
        years={financialYearOptions}
        financialYearOptions={financialYearOptions}
        lockFinancialYearSelection
        syncActiveFinancialYear={false}
        categories={categories}
        personUsers={personUsers}
        isDerasarAdmin
        saving={saving}
        editMode={!!editingKey}
        minDate="1970-01-01"
        maxDate={openingOfferDateMax || ''}
        onClose={closeForm}
        onSubmit={handleSubmit}
      />
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3>Offering Opening Balances</h3>
        {canWrite && (
          <button className="btn btn-ghost btn-sm" onClick={openAdd} disabled={saving}>
            <FiPlus /> Add Offering
          </button>
        )}
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Offer No</th>
              <th>Member</th>
              <th>Category</th>
              <th>Date</th>
              <th style={{ textAlign: 'right' }}>Offers (Rs)</th>
              <th style={{ textAlign: 'right' }}>Paid (Rs)</th>
              <th style={{ textAlign: 'right' }}>Outstanding</th>
              <th>Status</th>
              {canWrite && <th style={{ width: 80 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {offeringRows.length === 0 && (
              <tr>
                <td colSpan={canWrite ? 10 : 9} className="empty-state">
                  {canWrite
                    ? 'No opening offerings yet. Click "Add Offering" to add one.'
                    : 'No opening offerings for this year.'}
                </td>
              </tr>
            )}

            {offeringRows.map(([key, line], index) => {
              const memberName = personUsers.find((person) => String(person.id) === String(line.userId))?.name
                || line.userName || '-';
              const categoryName = categories.find((category) => String(category.id) === String(line.donationCategoryId))?.name || '-';
              const amount = Number(line.pendingAmountInr || 0);

              return (
                <tr key={key}>
                  <td>{index + 1}</td>
                  <td>
                    <code style={{ fontSize: '0.82rem' }}>{line.offerNo || '—'}</code>
                  </td>
                  <td>
                    <div className={tableStyles.memberCell}>
                      <span>{memberName}</span>
                    </div>
                  </td>
                  <td>{categoryName}</td>
                  <td>{line.originalOfferingDate ? formatDate(line.originalOfferingDate) : '-'}</td>
                  <td style={{ textAlign: 'right' }}><strong>Rs {formatMoney(amount)}</strong></td>
                  <td style={{ textAlign: 'right' }}>Rs {formatMoney(0)}</td>
                  <td style={{ textAlign: 'right' }}>Rs {formatMoney(amount)}</td>
                  <td><span className="badge badge-pending">pending</span></td>
                  {canWrite && (
                    <td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => openEdit(key, line)} disabled={saving}>
                          <FiEdit2 size={14} />
                        </button>
                        <button className="btn btn-ghost btn-sm" title="Remove" onClick={() => onRemoveRow(key)} disabled={saving}>
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} style={{ textAlign: 'right' }}>
                <strong>Total Opening Balance</strong>
              </td>
              <td style={{ textAlign: 'right' }}><strong>Rs {formatMoney(total)}</strong></td>
              <td style={{ textAlign: 'right' }}><strong>Rs {formatMoney(0)}</strong></td>
              <td style={{ textAlign: 'right' }}><strong>Rs {formatMoney(total)}</strong></td>
              <td />
              {canWrite && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
